import * as THREE from 'three';
import { fromJS, Map, List } from 'immutable';
import createReducer from './utils/createReducer';
import { get, getWorldTransform, set, find } from './scene';
import { queueAnimation } from './animation';
import { touch, virtualCameraId, vrCameraId, virtualTargetId, merge } from './sceneGraph';
import updateCameraLookAtTarget from '../translators/utils/updateCameraLookAtTarget';
import calculateBoundingBox from '../translators/utils/calculateBoundingBox';
import { initCamera } from '../translators/camera';
import setMatrixOnNode from '../translators/utils/setMatrixOnNode';
import decompose from '../translators/utils/decompose';
import getTargetWorldMatrix from '../translators/utils/getTargetWorldMatrix';
import { addCommand, deactivateCommand, removeCommand, updateCommand } from './commands';

export const RENDER_MODES = {
  normal: 'normal',
  vr: 'vr',
  lenticular: 'lenticular',
};

/**
 * This module supports the UI for the player.
 *
 * @module player
 * @public
 */

export const manipulators = {
  orbit: 'orbit',
  pan: 'pan',
  zoom: 'zoom',
  nodeRotate: 'nodeRotate',
  nodeMove: 'nodeMove',
  nodeScale: 'nodeScale',
};

const USE_MANIPULATOR = 'USE_MANIPULATOR';
const SET_CAMERA = 'SET_CAMERA';
const SET_PREV_CAMERA = 'SET_PREV_CAMERA';
const SET_CAMERA_ATTRS = 'SET_CAMERA_ATTRS';
const SET_CAMERA_POSITION = 'SET_CAMERA_POSITION';
const SET_RECT = 'SET_RECT';
const DISPLAY_THUMBNAIL = 'DISPLAY_THUMBNAIL';
const SET_ACTIVE_COLOR = 'SET_ACTIVE_COLOR';
const SET_HOVER_COLOR = 'SET_HOVER_COLOR';
const SET_PLAYER_ELEMENT = 'SET_PLAYER_ELEMENT';
const REQUEST_FULL_SCREEN = 'REQUEST_FULL_SCREEN';
const EXIT_FULL_SCREEN = 'EXIT_FULL_SCREEN';
const SET_FULL_SCREEN = 'SET_FULL_SCREEN';
const SET_RENDER_MODE = 'SET_RENDER_MODE';

const REQUEST_NORMAL_MODE = 'REQUEST_NORMAL_MODE';
const REQUEST_LENTICULAR_MODE = 'REQUEST_LENTICULAR_MODE';
const SET_DEVICE_TRACKING = 'SET_DEVICE_TRACKING';
const SET_BACKGROUND_COLOR = 'SET_BACKGROUND_COLOR';
const REQUEST_STEREO_MODE = 'REQUEST_STEREO_MODE';
const REQUEST_PARALLAX_MODE = 'REQUEST_PARALLAX_MODE';
const EXIT_STEREOPARALLAX_MODE = 'EXIT_STEREOPARALLAX_MODE';
const SET_CONSTRAIN_CAMERA_RADIUS = 'SET_CONSTRAIN_CAMERA_RADIUS';
const SET_THREE_SCENE = 'SET_THREE_SCENE';

const initialState = Map({
  activeColor: '#ffc85f',
  hoverColor: '#C08618',
  fullscreenBackgroundColor: '#FFF',
  activeManipulator: manipulators.orbit,
  fullScreen: false,
  renderingMode: RENDER_MODES.normal,
  deviceTracking: false,
  camera: Map({
    polarAngle: null, azimuthAngle: null, boundingRadius: null,
  }),
  rect: Map({ width: 400, height: 300 }),
  initialCameraWorldMatrix: null,
  initialTargetWorldMatrix: null,
  cameraWorldMatrix: null,
  displayThumbnail: true,
  playerElement: null,
  canvasElement: null,
  activeCamera: virtualCameraId,
  vrCamera: vrCameraId,
  threeScene: null,
  prevCamera: null,
  orientationHandler: null,
  constrainCameraRadius: null,
});

const reducer = createReducer(initialState, {
  [USE_MANIPULATOR](state, manipulator) {
    return state.set('activeManipulator', manipulator);
  },

  [SET_CAMERA](state, { id, prevCameraId }) {
    return state.set('activeCamera', id).set('prevCamera', prevCameraId);
  },

  [SET_CAMERA_ATTRS](state, attrs) {
    return state.mergeIn(['camera'], attrs);
  },

  [SET_RECT](state, rect) {
    return state.set('rect', rect);
  },

  [SET_CAMERA_POSITION](state, attrs) {
    return state.merge(attrs);
  },

  [SET_PLAYER_ELEMENT](state,  { playerEl, canvasEl }) {
    return state.set('playerElement', playerEl).set('canvasElement', canvasEl);
  },

  [DISPLAY_THUMBNAIL](state, display) {
    return state.set('displayThumbnail', !!display);
  },

  [SET_ACTIVE_COLOR](state, color) {
    return state.set('activeColor', color);
  },

  [SET_HOVER_COLOR](state, color) {
    return state.set('hoverColor', color);
  },

  [SET_BACKGROUND_COLOR](state, color) {
    return state.set('fullscreenBackgroundColor', color);
  },

  [REQUEST_FULL_SCREEN](state) {
    return state.set('fullScreen', true);
  },

  [EXIT_FULL_SCREEN](state) {
    return state.set('fullScreen', false);
  },

  [SET_FULL_SCREEN](state, fullScreen) {
    return state.set('fullScreen', fullScreen);
  },

  [SET_RENDER_MODE](state, mode) {
    return state.set('renderingMode', mode);
  },

  [REQUEST_LENTICULAR_MODE](state, { trackDevice, cameraId }) {
    return state.merge({
      renderingMode: 'lenticular',
      deviceTracking: trackDevice,
      prevCamera: cameraId,
    });
  },

  [REQUEST_NORMAL_MODE](state) {
    return state.merge({
      renderingMode: 'normal',
      deviceTracking: false,
    });
  },

  [SET_DEVICE_TRACKING](state, deviceTracking) {
    return state.set('deviceTracking', deviceTracking);
  },

  [SET_CONSTRAIN_CAMERA_RADIUS](state, radius) {
    return state.set('constrainCameraRadius', radius);
  },

  [SET_THREE_SCENE](state, { scene, camera }) {
    return state.set('threeScene', scene).set('threeCamera', camera);
  },
});

export function useManipulator(manipulator) {
  return { type: USE_MANIPULATOR, payload: manipulator };
}

export function getActiveManipulator(store) {
  return store.getIn(['player', 'activeManipulator']);
}


/**
 * Add a tool (See {@tutorial sdk-Tools})
 *
 * @public
 * @param {Tool} Tool
 * @param {String} name provide a name for the tool to allow removing it.
 * @returns {Promise}
 *
 */
export function addTool(tool, name) {
  return addCommand({ tool, enabled: true }, name);
}

/**
 * Remove a tool (See {@tutorial sdk-Tools})
 *
 * @public
 * @param {String} name The name of the tool to remove
 * @returns {Promise}
 */
export function removeTool(name) {
  return removeCommand(name);
}

export function showTool(name) {
  return updateCommand(name, { display: { playerTools: true } });
}

export function hideTool(name) {
  return updateCommand(name, { display: { playerTools: false } });
}

export function updateToolOptions(name, options) {
  return { type: UPDATE_COMMAND_OPTIONS, payload: { name, options } };
}

export function getRect(store) {
  return store.getIn(['player', 'rect']);
}

export function setRect(rect) {
  return { type: SET_RECT, payload: rect };
}

export function setCameraAttrs(attrs) {
  return { type: SET_CAMERA_ATTRS, payload: attrs };
}

export function getCameraAttrs(store) {
  return store.getIn(['player', 'camera']);
}

/**
 * Returns the world matrix of the active camera
 */
export function getCameraWorldTransform(store) {
  return getWorldTransform(store, store.getIn(['player', 'activeCamera']));
}

/**
 * This sets the scene to use the camera.
 */
export function useCamera(id) {
  return (store) => {
    // store.dispatch(setCamera(id, getCamera(store)));
    return store.dispatch(set({ scene: true, plug: 'Player', property: 'camera' }, id));
  };
}

export function useVrCamera(switchToVR) {
  return (store) => {
    if (switchToVR) {
      const activeCamera = store.getIn(['player', 'activeCamera']);
      store.dispatch(setCamera(vrCameraId, activeCamera));
    } else {
      const prevCamera = store.getIn(['player', 'prevCamera']) || virtualCameraId;
      store.dispatch(setCamera(prevCamera, null));
    }
  };
}

/**
 * Sets the active camera for the scene
 * @private
 */
export function setCamera(id, prevCameraId) {
  return (store) => {
    const target = getTargetWorldMatrix(store, prevCameraId);
    const oldCamData = get(store, { id: prevCameraId, evalPlug: 'Camera' });
    const oldCameraTransform = get(store, { id: prevCameraId, evalPlug: 'Transform' });
    if (oldCameraTransform && !oldCameraTransform.target) {
      oldCamData.targetWorldMatrix = target;
    }

    const newCamData = get(store, { id, evalPlug: 'Camera' });
    if (newCamData.targetWorldMatrix) {
      store.dispatch(merge([virtualTargetId, 'Transform', 0], decompose(newCamData.targetWorldMatrix)));
      newCamData.targetWorldMatrix = null;
    }

    store.dispatch({ type: SET_CAMERA, payload: { id, prevCameraId } });
  };
}

/**
 * @private
 */
export function setThreeScene(scene, camera) {
  return { type: SET_THREE_SCENE, payload: { scene, camera } };
}

export function getThreeScene(store) {
  return store.getIn(['player', 'threeScene']);
}

export function getThreeCamera(store) {
  return store.getIn(['player', 'threeCamera']);
}

export function getBoundingBox(store) {
  const threeScene = getThreeScene(store);
  return calculateBoundingBox(threeScene);
}

/**
 * Returns the current active camera
 * @public
 */
export function getCamera(store) {
  return store.getIn(['player', 'activeCamera']);
}

export function getTools(store) {
  return store.getIn(['player', 'tools']);
}

export function setCameraPosition(attrs) {
  return { type: SET_CAMERA_POSITION, payload: attrs };
}

export function getInitialCameraPosition(store) {
  return {
    initialCameraWorldMatrix: store.getIn(['player', 'initialCameraWorldMatrix']),
    initialTargetWorldMatrix: store.getIn(['player', 'initialTargetWorldMatrix']),
  };
}

/**
 *
 * Enable fullscreen mode.
 * @public
 *
 */
export function requestFullscreen() {
  return (store) => {
    const playerEl = store.getIn(['player', 'playerElement']);
    ['requestFullscreen', 'webkitRequestFullscreen', 'mozRequestFullScreen', 'msRequestFullscreen'].forEach(function (fn) {
      if (playerEl[fn]) return playerEl[fn]();
    });

    store.dispatch({ type: REQUEST_FULL_SCREEN });
  };
}

/**
 * Exit fullscreen mode
 * @public
 */
export function exitFullscreen() {
  return (store) => {
    ['exitFullscreen', 'webkitExitFullscreen', 'mozCancelFullScreen', 'msExitFullscreen'].forEach(function (fn) {
      if (document[fn]) return document[fn]();
    });

    return store.dispatch({ type: EXIT_FULL_SCREEN });
  };
};

/**
 * Toggle fullscreen mode
 * @public
 *
 */
export function toggleFullscreen() {
  return (store) => {
    return store.dispatch(isFullscreen(store) ? exitFullscreen() : requestFullscreen());
  };
};

/**
 * Check whether were in fullscreen mode.
 * @public
 *
 */
export function isFullscreen(store) {
  return store.getIn(['player', 'fullScreen']);
};

/**
 * Set the value of fullscreen (set on an event handler, if the native
 * browser control is used to escape full screen mode.
 */
export function setFullscreen(fullScreen) {
  return (store) => {
    if (isFullscreen(store) !== fullScreen) {
      store.dispatch({ type: SET_FULL_SCREEN, payload: fullScreen });
    }

    if (!fullScreen && isVRMode(store)) {
      store.dispatch(deactivateCommand('vrMode'));
    }
  };
};

export function getRenderingMode(store) {
  return store.getIn(['player', 'renderingMode']);
}

/**
 * @public
 *
 */
export function isNormalRenderingMode(store) {
  return (store.getIn(['player', 'renderingMode']) === RENDER_MODES.normal);
};

/**
 * Check whether we are in VR mode.
 * @public
 *
 */
export function isVRMode(store) {
  return (store.getIn(['player', 'renderingMode']) === RENDER_MODES.vr);
};

/**
 * Check whether we are in Lenticular rendering mode.
 * @public
 */
export function isLenticularMode(store) {
  return (store.getIn(['player', 'renderingMode']) === RENDER_MODES.lenticular);
};

export function activateTool(toolName) {
  return (store) => {
    const tool = store.getIn(['player', 'tools', toolName]);
    if (!tool) return;
    if (tool.get('action')) return tool.get('action')(store);
    const isActive = _isToolActive(store, tool);
    if (tool.get('activate') && !isActive) return tool.get('activate')(store);
    if (tool.get('deactivate') && isActive) return tool.get('deactivate')(store);
    return store.dispatch(useManipulator(toolName));
  };
}

function _isToolActive(store, tool) {
  if (tool.get('isActive')) return tool.get('isActive')(store);
  return store.getIn(['player', 'activeManipulator']) === tool.get('name');
}

export function isToolActive(store, toolName) {
  const tool = store.getIn(['player', 'tools', toolName]);
  return tool && _isToolActive(store, tool);
}

export function getActiveTools(store) {
  return store.getIn(['player', 'tools']).filter((tool) => _isToolActive(store, tool));
}

export function requestLenticularMode({ trackDevice }) {
  return (store) => {
    store.dispatch(requestFullscreen());
    const cameraId = getCamera(store);
    store.dispatch({ type: REQUEST_LENTICULAR_MODE, payload: { trackDevice, cameraId } });
  };
};

export function requestNormalMode() {
  return { type: REQUEST_NORMAL_MODE };
};

export function getPrevCamera(store) {
  return store.getIn(['player', 'prevCamera']);
}

export function setRenderMode(mode) {
  if (!RENDER_MODES[mode]) throw new Error(`Invalid Render Mode: ${mode}`);
  return { type: SET_RENDER_MODE, payload: mode };
}

export function toggleLenticularMode() {
  return (store) => {
    store.dispatch(isLenticularMode(store) ?  requestNormalMode() : requestLenticularMode({}));
  };
};

export function enableDeviceTracking() {
  return { type: SET_DEVICE_TRACKING, payload: true };
};

export function disableDeviceTracking() {
  return { type: SET_DEVICE_TRACKING, payload: false };
};

export function toggleDeviceTracking() {
  return (store) => {
    console.log('toggle device tracking', !isDeviceTracking(store));
    store.dispatch(isDeviceTracking(store) ?  disableDeviceTracking() : enableDeviceTracking());
  };
};

export function isDeviceTracking(store) {
  return store.getIn(['player', 'deviceTracking']);
};


/**
 * Store the rendered player element
 */
export function setPlayerElement(playerEl, canvasEl) {
  return { type: SET_PLAYER_ELEMENT, payload: { playerEl, canvasEl } };
};

/**
 * Set the active color and timeline color to be used for the player css.
 *
 * @public
 * @param {Color} color a valid css color (ex. '#ff0000'}.
 * @returns {Promise}
 *
 */
export function setActiveColor(color) {
  return { type: SET_ACTIVE_COLOR, payload: color };
};

/**
 * Set the hover color to be used for the player css.
 *
 * @public
 * @param {Color} color a valid css color (ex. '#ff0000'}.
 * @returns {Promise}
 *
 */
export function setHoverColor(color) {
  return { type: SET_HOVER_COLOR, payload: color };
};

/**
 * Set the fullscreen background color to be used for the player css.
 *
 * @public
 * @param {Color} color a valid css color (ex. '#ff0000'}.
 * @returns {Promise}
 *
 */
export function setFullscreenBGColor(color) {
  return { type: SET_BACKGROUND_COLOR, payload: color };
};

/**
 * Return the active color.
 *
 * @public
 */
export function getActiveColor(store) {
  return store.getIn(['player', 'activeColor']);
};

/**
 * Return the hover color.
 *
 * @public
 */
export function getHoverColor(store) {
  return store.getIn(['player', 'hoverColor']);
};

/**
 * Return the fullscreen background color.
 *
 * @public
 */
export function getFullscreenBGColor(store) {
  return store.getIn(['player', 'fullscreenBackgroundColor']);
};

function slerpTween(startMatrix, endMatrix) {
  const startPosition = new THREE.Vector3();
  const startQuaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3(1, 1, 1);
  startMatrix.decompose(startPosition, startQuaternion, scale);

  const endPosition = new THREE.Vector3();
  const endQuaternion = new THREE.Quaternion();
  endMatrix.decompose(endPosition, endQuaternion, scale);

  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  scale.set(1, 1, 1);
  const matrix = new THREE.Matrix4();

  return function (time, start, end, duration) {
    position.lerpVectors(startPosition, endPosition, time / duration);
    THREE.Quaternion.slerp(startQuaternion, endQuaternion, quaternion, time / duration);
    matrix.compose(position, quaternion, scale);
    return matrix;
  };
};

/**
 * Animate the camera to the position of another.
 *
 * @public
 * @param {String} id - the id of the camera to move to.
 * @param {Number} duration - length of the animation (ms), default 300.
 * @returns {Promise}
 */
export function animateCameraTo(id, duration) {
  return (store) => {
    const cameraId = getCamera(store);
    if (!cameraId || !id) return;

    let prep = Promise.resolve();

    // if camera is targeted, temporarily disable targeting during animation
    const camTarget = find(store, { id: cameraId, plug: 'Transform', property: 'target' });
    let targetingDisabled = false;
    if (camTarget && get(store, { id: cameraId, plug: 'Camera', property: 'targeted' })) {
      const target = get(store, camTarget);
      if (target) {
        prep = store.dispatch(set({ id: cameraId, plug: 'Camera', property: 'targeted' }, false));
        targetingDisabled = true;
      }
    }

    return prep.then(() => {
      const currentCameraTransform = getWorldTransform(store, cameraId);
      const newCameraTransform = initCamera(store, false, id);
      const actualDuration = duration || 300;

      return new Promise((resolve, reject) => {
        store.dispatch(queueAnimation({
          autoplay: true,
          name: 'Move Camera',
          iterations: 1,
          tracks: [{
            path: { id: cameraId, evalPlug: 'Transform' },
            duration: actualDuration,
            value: newCameraTransform,
            startValue: currentCameraTransform,
            tween: slerpTween(currentCameraTransform, newCameraTransform),
            set: (_path, to) => {
              setMatrixOnNode(store, cameraId, to);
            },
          },],
          onEnd: () => {
            resolve(store.dispatch(useCamera(id)).then(() => {
              // if camera was targeted, re-enable targeting now that animation is over
              return targetingDisabled ? store.dispatch(set({ id: cameraId, plug: 'Camera', property: 'targeted' }, true)) : null;
            }));
          },
        }));
      });
    });
  };
};


/**
 * Trigger a resize event. If you manually resize the player's dom element,
 * use this to force the player to rerender with the updated size.
 *
 * @public
 */
export function resize() {
  return (store) => {
    const translator = store.getTranslator();
    if (translator) translator.resize();
  };
};

/**
 * Given a manipulator event, return the list of node ids at that position
 *
 * @public
 * @param {Event} event generated by the manipulator interface. (See {@tutorial sdk-Tools})
 * @param {String} [rootNode] Id of root of nodes that will be checked against position
 * @returns {Array} list of node ids found.
 *
 * @example
 *
 * api.player.addTool({
 *   click: (ev) => {
 *     var nodes = api.player.filterNodesFromPosition(ev);
 *     console.log('Found: ', nodes.map((id) => api.scene.get({id: id, property: 'name'})));
 *   }
 * }, 'FindNode');
 *
 */
export function filterNodesFromPosition(store, event, rootNode) {
  const translator = store.getTranslator();
  if (!translator || !translator.raycastSelect) return null;
  const x = event.clientX / (event.rect.width / 2) - 1;
  const y = -(event.clientY / (event.rect.height / 2) - 1);
  const hits = translator.raycastSelect({ x, y }, rootNode);
  return hits.map((hit) => hit.id);
};


/**
 * return the list of node ids at the center of the screen
 *
 * @public
 * @param {String} [rootNode] Id of root of nodes that will be checked against center
 * @returns {Array} list of node ids found.
 */
export function filterNodesFromCenter(store, rootNode) {
  const translator = store.getTranslator();
  if(!translator || !translator.raycastSelect) return null;
  const hits = translator.raycastSelect({x:0, y:0}, rootNode);
  return hits.map((hit) => hit.id);
};

/**
* Will constrain the target of the camera to remain inside the bounding sphere of the scene.
* The camera will not be allowed to enter the bounding sphere of the scene nor leave  bounding sphere defined by arguments
*
* @public
* @param {Number} radius distance around the center of the scene that defineds outer bounding sphere, setting radius to null disables bounding spheres
*/
export function setCameraRadiusConstraint(radius) {
  return (store) => {
    store.dispatch({ type: SET_CONSTRAIN_CAMERA_RADIUS, payload: radius });
    const cameraId = getCamera(store);
    if (!cameraId) return;

    // Store the radius constraint in the camera data, and touch the cameraNode
    // to rerender
    const cameraData = get(store, { id:  cameraId, evalPlug: 'Camera' });
    if (cameraData) {
      if (!cameraData.radiusConstraint || cameraData.radiusConstraint.radius !== radius) {
        cameraData.newRadiusConstraint = radius;
      }
    }

    store.dispatch(touch(cameraId));
  };
}

export function getCanvasElement(store) {
  return store.getIn(['player', 'canvasElement']);
}

export function getCameraRadiusConstraint(store) {
  return store.getIn(['player', 'constrainCameraRadius']);
};

/**
 * Turn thumbnail display on or off
 * @public
 * @param {Boolean} display - Display thumbnail (default true)
 */
export function displayThumbnail(display) {
  return { type: DISPLAY_THUMBNAIL, payload: display };
};

export function shouldDisplayThumbnail(store) {
  return store.getIn(['player', 'displayThumbnail']);
};

const publicApi = {
  reducer,
  actions: { addTool, removeTool, showTool, hideTool, useManipulator,
    setCameraAttrs, useCamera, setCameraPosition, animateCameraTo, resize,
    displayThumbnail, setActiveColor, requestFullscreen, exitFullscreen, setFullscreen,
    toggleFullscreen, setHoverColor, requestLenticularMode,
    enableDeviceTracking, disableDeviceTracking, toggleDeviceTracking,
    requestNormalMode, setFullscreenBGColor, setCameraRadiusConstraint, updateToolOptions, setCamera
  },
  selectors: { getCamera, getCameraAttrs, getInitialCameraPosition, getCameraWorldTransform,
    getActiveColor, isFullscreen, isVRMode, isLenticularMode, getFullscreenBGColor,
    isDeviceTracking, shouldDisplayThumbnail, filterNodesFromPosition, filterNodesFromCenter,
    getThreeScene, getThreeCamera,
  },
};
export default publicApi;



// WEBPACK FOOTER //
// ./src/modules/player.js